import { useCallback, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import { ESIGN_CONSENT_TEXT, ESIGN_CONSENT_VERSION, ESIGN_INTENT_TEXT } from '@policy-tracker/types';
import { PolicyService, SignerService } from '../services/api';
import { useUserType } from '../hooks/useUserType';
import { useAsyncData } from '../hooks/useAsyncData';
import { getIsDemo, getBrandingBaseUrl } from '../amplify-config';
import HelpLink from '../components/HelpLink';
import LoadingPage from '../components/LoadingPage';

const DECLINE_REASON_MAX = 500;

/**
 * Builds the marketing signup URL the demo terminal CTA links to (#2765),
 * carrying UTM attribution so the branding site can fire `demo_cta_clickthrough`,
 * plus `ref=demo` (#3281) so the branding site forwards it to the TM `/signup`
 * endpoint and the DemoSignupAttributed funnel metric can be attributed.
 */
function demoSignupUrl(): string {
    return `${getBrandingBaseUrl()}/signup?utm_source=demo&utm_medium=signer_demo&ref=demo`;
}

/** Long-form date for the "you signed this on <date>" record line (#2796). */
function formatSignedDate(iso: string): string {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

function SignByAuth() {
    const { id } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const assignmentId = searchParams.get('assignmentId') ?? undefined;
    const navigate = useNavigate();
    const { isAdmin } = useUserType();
    const [signing, setSigning] = useState(false);
    // Post-action latches. `signed`/`declined` are NOT pure functions of the
    // fetched data: five of their eight writers are imperative transitions
    // after a sign, a decline, or a 409 whose terminal state exists only in
    // the error response and never in any fetched data. The load-time terminal
    // state is derived below and the latch is layered over it (#3423).
    const [signedLatch, setSignedLatch] = useState(false);
    const [consentChecked, setConsentChecked] = useState(false);
    // Download Certificate state (issue #1148). Visible after a successful
    // sign — pulls the assignment's Certificate of Completion PDF.
    const [downloadingCertificate, setDownloadingCertificate] = useState(false);
    const [certificateError, setCertificateError] = useState<string | null>(null);
    // Decline-flow state (issue #1146).
    const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
    const [declineReason, setDeclineReason] = useState('');
    const [declining, setDeclining] = useState(false);
    const [declinedLatch, setDeclinedLatch] = useState(false);
    // #2796 — set when the page loads with the assignment ALREADY signed (a
    // repeat visit, e.g. a returning demo prospect's magic link). Carries the
    // acknowledgedAt timestamp so the terminal state can show the durable
    // record ("you signed this on <date>") instead of a fresh sign form.

    const loadPolicy = useCallback(async () => {
        if (!id) throw new Error('Policy ID is required');

        // #2796 — best-effort terminal-state lookup: if this signer already
        // signed or declined the assignment, land directly on the record
        // rather than re-presenting the sign form. Any failure here degrades
        // to the form (the sign action still 409s into the terminal state).
        const fetchPortal = async () => {
            try {
                return await SignerService.getAssignments();
            } catch {
                return undefined;
            }
        };

        const [detail, portal] = await Promise.all([PolicyService.getPolicy(id), fetchPortal()]);
        return { detail, portal };
    }, [id]);

    const { data, error: loadFailure, loading: fetchLoading } = useAsyncData(id ?? null, loadPolicy);

    const policy = data?.detail ?? null;
    // A missing route id is an error, not a fetch: it used to be reported from
    // inside `loadPolicy` before it ever called the API.
    const loadError = !id ? 'Policy ID is required' : loadFailure !== null ? 'Failed to load the policy. Please try again.' : null;
    const loading = !id ? false : fetchLoading;

    // Load-time terminal state, derived from the portal lookup. Without an
    // assignmentId the match falls back to the policyId — but a recurring
    // policy can hold BOTH an old signed record and a new pending assignment;
    // the pending one wins (the signer needs the form), so only match terminal
    // when nothing is pending.
    const portal = data?.portal;
    const pendingForPolicy = (portal?.pending ?? []).some((a) => a.policyId === id);
    const terminal = [...(portal?.completed ?? []), ...(portal?.declined ?? [])].find((a) =>
        assignmentId ? a.assignmentId === assignmentId : a.policyId === id && !pendingForPolicy
    );
    // #2796 — set when the page loads with the assignment ALREADY signed (a
    // repeat visit, e.g. a returning demo prospect's magic link). Carries the
    // acknowledgedAt timestamp so the terminal state can show the durable
    // record ("you signed this on <date>") instead of a fresh sign form.
    const signedOnLoadAt = terminal?.status === 'signed' ? terminal.acknowledgedAt : undefined;

    const signed = signedLatch || terminal?.status === 'signed';
    const declined = declinedLatch || terminal?.status === 'declined';

    // `error` is written by seven imperative action paths as well as the load,
    // so it is keyed to the load failure it was written against.
    const [errorOverride, setErrorOverride] = useState<{ base: unknown; value: string | null } | null>(null);
    const error = errorOverride !== null && errorOverride.base === loadFailure ? errorOverride.value : loadError;

    const setError = useCallback(
        (value: string | null) => {
            setErrorOverride({ base: loadFailure, value });
        },
        [loadFailure]
    );

    const handleSign = async () => {
        if (!id) return;
        // Defense-in-depth — the button is disabled until checked, but never
        // submit if state somehow says otherwise.
        if (!consentChecked) return;

        try {
            setSigning(true);
            await PolicyService.acknowledgePolicy(id, assignmentId, true, ESIGN_CONSENT_VERSION);
            setSignedLatch(true);
        } catch (err: unknown) {
            const response =
                err instanceof Object && 'response' in err ? (err.response as { statusCode?: number; body?: { json: () => Promise<unknown> } }) : undefined;
            const status = response?.statusCode;
            if (status === 409) {
                // Server discriminates terminal state via the response body's
                // `status` field. Default to 'signed' if the body isn't readable
                // (back-compat with the legacy AlreadyAcknowledgedError shape).
                let bodyStatus: string | undefined;
                try {
                    const body = await response?.body?.json();
                    bodyStatus = body && typeof body === 'object' && 'status' in body ? (body as { status?: string }).status : undefined;
                } catch {
                    /* fall through to default 'signed' state */
                }
                if (bodyStatus === 'declined') {
                    setDeclinedLatch(true);
                } else {
                    setSignedLatch(true);
                }
                return;
            }
            if (status === 404) {
                setError('No assignment found for this policy. You may not have been assigned to sign it.');
                return;
            }
            if (status === 403) {
                setError('Your organization has reached its acknowledgment limit for the current period. Please contact your administrator.');
                return;
            }
            setError('Failed to sign the policy. Please try again.');
        } finally {
            setSigning(false);
        }
    };

    const handleDeclineConfirm = async () => {
        if (!id) return;
        try {
            setDeclining(true);
            await PolicyService.declinePolicy(id, assignmentId, declineReason);
            setDeclineDialogOpen(false);
            setDeclinedLatch(true);
        } catch (err: unknown) {
            const status = err instanceof Object && 'response' in err ? (err.response as { statusCode?: number })?.statusCode : undefined;
            // 409 — already signed/declined/no-longer-pending. The server has
            // surfaced a terminal state; show the same terminal UI we'd show
            // on a fresh decline so the magic-link revisit case Just Works.
            if (status === 409) {
                setDeclineDialogOpen(false);
                setDeclinedLatch(true);
                return;
            }
            if (status === 410) {
                setDeclineDialogOpen(false);
                setError('This signing link is no longer valid.');
                return;
            }
            if (status === 404) {
                setDeclineDialogOpen(false);
                setError('No assignment found for this policy.');
                return;
            }
            setDeclineDialogOpen(false);
            setError('Failed to decline the policy. Please try again.');
        } finally {
            setDeclining(false);
        }
    };

    if (loading) {
        return <LoadingPage message="Loading policy..." />;
    }

    if (error) {
        return (
            <Box className="page-content">
                <Typography variant="h4" component="h2" gutterBottom>
                    Sign Policy
                </Typography>
                <Alert severity="error" role="alert" aria-live="assertive" sx={{ mb: 2 }}>
                    {error}
                </Alert>
                <Button variant="outlined" onClick={() => navigate('/signer')}>
                    Back to My Policies
                </Button>
            </Box>
        );
    }

    const handleDownloadCertificate = async () => {
        if (!assignmentId) {
            setCertificateError('Certificate is not available for this signing session. Please access the certificate from the signer portal.');
            return;
        }
        setCertificateError(null);
        setDownloadingCertificate(true);
        try {
            await PolicyService.downloadCertificate(assignmentId);
        } catch {
            setCertificateError('Failed to download the certificate. Please try again.');
        } finally {
            setDownloadingCertificate(false);
        }
    };

    if (signed) {
        return (
            <Box className="page-content" sx={{ textAlign: 'center', py: 6 }}>
                {/*
                  #2796 — repeat visit: the assignment was already signed when
                  the page loaded, so present the durable acknowledgment record
                  (with its date) rather than a fresh-sign success message.
                */}
                {signedOnLoadAt ? (
                    <>
                        <Typography variant="h4" component="h2" color="success.main" gutterBottom>
                            Policy Signed
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                            You signed <strong>{policy?.title}</strong> on {formatSignedDate(signedOnLoadAt)}. Your acknowledgment is on record.
                        </Typography>
                    </>
                ) : (
                    <>
                        <Typography variant="h4" component="h2" color="success.main" gutterBottom>
                            Policy Signed Successfully
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                            Thank you for acknowledging <strong>{policy?.title}</strong>.
                        </Typography>
                    </>
                )}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mt: 3 }}>
                    {assignmentId && (
                        <Button variant="outlined" onClick={handleDownloadCertificate} disabled={downloadingCertificate}>
                            {downloadingCertificate ? 'Downloading…' : 'Download Certificate'}
                        </Button>
                    )}
                    {/*
                      #2765 — demo-tenant signers have no Signer Portal to return
                      to (the experience is confined to this sample-policy flow),
                      so the terminal CTA invites them to sign up for the full
                      product instead of navigating to `/signer`. Real tenants'
                      signers keep the normal "Back to My Policies" button.
                    */}
                    {getIsDemo() ? (
                        <Button variant="contained" href={demoSignupUrl()} component="a">
                            Want this for your team? Get started free
                        </Button>
                    ) : (
                        <Button variant="contained" onClick={() => navigate('/signer')}>
                            Back to My Policies
                        </Button>
                    )}
                </Box>
                {certificateError && (
                    <Alert severity="error" role="alert" aria-live="assertive" sx={{ mt: 2, textAlign: 'left' }}>
                        {certificateError}
                    </Alert>
                )}
            </Box>
        );
    }

    if (declined) {
        return (
            <Box className="page-content" sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    You have declined this assignment
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                    Your administrator has been notified. No further action is needed from you.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mt: 3 }}>
                    {assignmentId && (
                        <Button variant="outlined" onClick={handleDownloadCertificate} disabled={downloadingCertificate}>
                            {downloadingCertificate ? 'Downloading…' : 'Download Certificate'}
                        </Button>
                    )}
                    <Button variant="contained" onClick={() => navigate(isAdmin ? '/policies' : '/signer')}>
                        Back to My Policies
                    </Button>
                </Box>
                {certificateError && (
                    <Alert severity="error" role="alert" aria-live="assertive" sx={{ mt: 2, textAlign: 'left' }}>
                        {certificateError}
                    </Alert>
                )}
            </Box>
        );
    }

    return (
        <Box className="page-content">
            <Typography variant="h4" component="h2" gutterBottom>
                {policy?.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Please review the policy document below before acknowledging.
            </Typography>
            <Box sx={{ mb: 2 }}>
                <HelpLink slug="assignments/signoff" />
            </Box>

            {policy?.presignedUrl && (
                <Box
                    component="iframe"
                    src={policy.presignedUrl}
                    title={policy.title}
                    sx={{
                        width: '100%',
                        // #2905 — on phones (<600px) a fixed 600px viewer pushed the
                        // ESIGN consent card + sign button below the 812px fold,
                        // violating the #1145 "visible without scrolling" AC. The
                        // header/title block consumes ~265px at 375px wide, so capping
                        // the viewer at min(55vh, 420px) keeps the consent card's top
                        // edge inside the first viewport while staying ≥~40vh for
                        // document readability. Desktop/tablet (≥sm) keep 600px.
                        height: { xs: 'min(55vh, 420px)', sm: '600px' },
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 3,
                        display: 'block',
                    }}
                />
            )}

            <Paper variant="outlined" sx={{ p: 3 }}>
                {/*
                  ESIGN Act affirmative-consent gate (issue #1145). The
                  checkbox is required before the sign button enables. The
                  exact wording lives in ESIGN_CONSENT_TEXT (one source of
                  truth) and is captured on the assignment record alongside
                  ESIGN_CONSENT_VERSION so we can prove which language each
                  signer agreed to if the wording changes later.
                */}
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={consentChecked}
                            onChange={(e) => setConsentChecked(e.target.checked)}
                            slotProps={{ input: { 'aria-label': ESIGN_CONSENT_TEXT } }}
                            // `sign-consent-checkbox` — tests/e2e/core-loop.spec.ts (#2335)
                            // checks this before clicking `sign-acknowledge-button`
                            // (the button is disabled until consent is checked).
                            // Applied on the Checkbox root because MUI v6 slotProps.input
                            // is type-narrowed and rejects arbitrary data-* attrs.
                            data-testid="sign-consent-checkbox"
                        />
                    }
                    label={ESIGN_CONSENT_TEXT}
                    sx={{ alignItems: 'flex-start', mb: 2, '& .MuiCheckbox-root': { pt: 0 } }}
                />

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {ESIGN_INTENT_TEXT}
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                        variant="contained"
                        onClick={handleSign}
                        disabled={signing || !consentChecked}
                        sx={{ px: 4, py: 1.5, fontSize: '1rem' }}
                        // `sign-acknowledge-button` — tests/e2e/core-loop.spec.ts (#2335)
                        data-testid="sign-acknowledge-button"
                    >
                        {signing ? 'Signing...' : 'I Acknowledge and Sign'}
                    </Button>
                    {/*
                      Decline button (issue #1146) — sibling to Sign so the
                      signer always has a non-destructive way to formally
                      refuse. Outlined, neutral color: this is a legitimate
                      choice, not an error or destructive action.
                    */}
                    <Button variant="outlined" color="inherit" onClick={() => setDeclineDialogOpen(true)} disabled={signing || declining}>
                        Decline
                    </Button>
                    <Button variant="outlined" onClick={() => navigate(isAdmin ? '/policies' : '/signer')}>
                        Cancel
                    </Button>
                </Box>
            </Paper>

            <Dialog open={declineDialogOpen} onClose={() => (declining ? null : setDeclineDialogOpen(false))} aria-labelledby="decline-dialog-title">
                <DialogTitle id="decline-dialog-title">Decline this assignment?</DialogTitle>
                <Box sx={{ px: 3, pb: 1, mt: -1 }}>
                    <HelpLink slug="assignments/exceptions" />
                </Box>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Your administrator will be notified that you have declined to sign this policy. You may optionally provide a reason.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        minRows={3}
                        maxRows={8}
                        label="Reason (optional)"
                        value={declineReason}
                        onChange={(e) => setDeclineReason(e.target.value)}
                        slotProps={{ htmlInput: { maxLength: DECLINE_REASON_MAX } }}
                        helperText={`${declineReason.length}/${DECLINE_REASON_MAX}`}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeclineDialogOpen(false)} disabled={declining}>
                        Cancel
                    </Button>
                    <Button onClick={handleDeclineConfirm} disabled={declining} variant="contained">
                        {declining ? 'Submitting...' : 'Submit Decline'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default SignByAuth;
